import { useNavigate, useLocation, useNavigationType, type NavigateFunction } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef } from "react";
import routes from "./config";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

const SCROLL_STORAGE_KEY = "koalabet.scroll.positions";

function readStoredPositions(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function writeStoredPositions(positions: Record<string, number>) {
  try {
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Ignore storage errors and continue with in-memory data.
  }
}

export function AppRoutes() {
  const element = useRoutes(routes);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const positionsRef = useRef<Record<string, number>>(readStoredPositions());
  const previousKeyRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const previousKey = previousKeyRef.current;
    if (previousKey) {
      positionsRef.current[previousKey] = window.scrollY;
      writeStoredPositions(positionsRef.current);
    }

    const savedY = positionsRef.current[location.key];
    const shouldRestore = navigationType === "POP" && typeof savedY === "number";

    window.requestAnimationFrame(() => {
      window.scrollTo(0, shouldRestore ? savedY : 0);
    });

    previousKeyRef.current = location.key;

    return () => {
      positionsRef.current[location.key] = window.scrollY;
      writeStoredPositions(positionsRef.current);
    };
  }, [location.key, navigationType]);

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(navigate);
  }, [navigate]);

  return element;
}
